import { pdf } from '@react-pdf/renderer';
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from 'sweetalert2';
import { mostraAlertaError, mostraAlertaOk, mostrarAlertaLoading } from "../../../../componentes/alerts/sweetAlert";
import { AxiosPrivado } from "../../../../componentes/axios/Axios";
import { ErroresPeticion } from "../../../../componentes/axios/ErroresPeticion";
import Card from "../../../../componentes/contenedores/Card";
import { IdentificarEstacion } from "../../../../componentes/estacion/Estacion";
import ModalBootstrap from "../../../../componentes/modal/ModalBootstrap";
import ModalWizardBootstrap from "../../../../componentes/modal/ModalWizardBootstrap";
import Page from "../../../../componentes/plantilla/Page";
import { CajaVerificar, ProductosListar, VentaAnulada, VentaListarId, VentaPagoAnulada } from "../../../../configuracion/apiUrls";
import { formatoMoneda } from "../../../../configuracion/validaciones";
import { useContextEmpleado } from "../../../../contexto/empleado/EmpleadoContext";
import { useContextUsuario } from "../../../../contexto/usuario/UsuarioContext";
import { ImprimirVenta } from "./ImprimirVentas";
import ReporteVentas from "./ReporteVentas";

const VerVenta = ({ ventaSeleccionada, setVentaSeleccionada }) => {
    const { usuario } = useContextUsuario();
    const { ActualizarLista, setListaProductos } = useContextEmpleado();

    // Estados de UI y modales
    const [modalOpen, setModalOpen] = useState(false);
    const [modalOpen2, setModalOpen2] = useState(false);
    const [loading, setLoading] = useState(false);

    // Desagregación de descripciones de anulación
    const [descripcionAnulacion, setDescripcionAnulacion] = useState("");
    const [descripcionAnulacion2, setDescripcionAnulacion2] = useState("");

    // Estados de pagos
    const [efectivo, setEfectivo] = useState(0);
    const [totalRecibido, setTotalRecibido] = useState(0);
    const [pos, setPos] = useState(0);
    const [transferencias, setTransferencias] = useState(0);

    const [montosPos, setMontosPos] = useState({});
    const [referenciasPos, setReferenciasPos] = useState({});
    const [erroresReferenciaPos, setErroresReferenciaPos] = useState({});

    const [montosTransferencia, setMontosTransferencia] = useState({});
    const [referenciasTransferencia, setReferenciasTransferencia] = useState({});
    const [erroresReferenciaTransferencia, setErroresReferenciaTransferencia] = useState({});

    const [cajaId, setCajaId] = useState(null);
    const [cajaAbierta, setCajaAbierta] = useState(false);

    const totalVenta = useMemo(() => {
        return parseFloat(ventaSeleccionada?.totalVenta || 0);
    }, [ventaSeleccionada]);

    // Verificar caja ÚNICAMENTE al montar el componente o abrir el modal de anulación
    const verificarYCargarCaja = useCallback(async () => {
        try {
            const estacionDatos = await IdentificarEstacion();
            const response = await AxiosPrivado.post(CajaVerificar, { estacionDatos });

            if (response.data.estado === "CE") {
                mostraAlertaError("La caja ya está cerrada. No se pueden registrar ventas o cambios.");
                setCajaAbierta(false);
                return false;
            }

            const caja = response.data;
            setCajaId(caja.id);
            setCajaAbierta(caja.estado !== "CE");
            return true;
        } catch (error) {
            ErroresPeticion(error);
            return false;
        }
    }, []);

    useEffect(() => {
        verificarYCargarCaja();
    }, [verificarYCargarCaja]);

    const [datosPago, setDatosPago] = useState({
        recibido: 0,
        tipoVenta: "Contado",
        documento: "FA",
        efectivo: [],
        transferencia: [],
        tarjeta: [],
        montoCredito: 0,
        fecha: "",
        fechaVencimiento: "",
        saldo: 0,
    });

    const actualizarListaPagos = useCallback(() => {
        let tipoPagoValido = "Efectivo";

        if (totalRecibido > 0) {
            if (parseFloat(efectivo) > 0 && transferencias === 0 && pos === 0) {
                tipoPagoValido = "Efectivo";
            } else if (transferencias > 0 && pos === 0) {
                tipoPagoValido = "Transferencia";
            } else if (pos > 0 && transferencias === 0) {
                tipoPagoValido = "Tarjeta";
            } else {
                tipoPagoValido = "Mixto";
            }
        }

        const tipoVenta = datosPago.documento === "CR" ? "Crédito" : "Contado";

        let nPago = {
            pago: totalVenta,
            recibido: totalRecibido,
            tipo: tipoPagoValido,
            documento: datosPago.documento,
            tipoVenta: tipoVenta,
            fecha: moment().format("YYYY-MM-DD"),
            efectivo: parseFloat(efectivo) > 0 ? [{ monto: parseFloat(efectivo) }] : [],
        };

        const listaTransferencias = Object.keys(montosTransferencia)
            .filter(cuentaId => parseFloat(montosTransferencia[cuentaId] || 0) > 0)
            .map(cuentaId => ({
                monto: parseFloat(montosTransferencia[cuentaId]),
                referencia: referenciasTransferencia[cuentaId] || "",
                cuentaId: parseInt(cuentaId, 10),
            }));

        if (listaTransferencias.length > 0) {
            nPago.transferencia = listaTransferencias;
        }

        const listaPos = Object.keys(montosPos)
            .filter(posId => parseFloat(montosPos[posId] || 0) > 0)
            .map(posId => ({
                monto: parseFloat(montosPos[posId]),
                referencia: referenciasPos[posId] || "",
                posId: parseInt(posId, 10),
            }));

        if (listaPos.length > 0) {
            nPago.tarjeta = listaPos;
        }

        setDatosPago(nPago);
    }, [totalRecibido, efectivo, transferencias, pos, datosPago.documento, totalVenta, montosTransferencia, referenciasTransferencia, montosPos, referenciasPos]);

    // Recalcular total recibido y sincronizar lista de pagos
    useEffect(() => {
        const nuevoTotal = parseFloat(pos || 0) + parseFloat(transferencias || 0) + parseFloat(efectivo || 0);
        if (nuevoTotal !== totalRecibido) {
            setTotalRecibido(nuevoTotal);
        }
        actualizarListaPagos();
    }, [pos, transferencias, efectivo, actualizarListaPagos, totalRecibido]);

    const limpiarDatosPago = () => {
        setEfectivo(0);
        setTotalRecibido(0);
        setPos(0);
        setTransferencias(0);
        setMontosPos({});
        setReferenciasPos({});
        setErroresReferenciaPos({});
        setMontosTransferencia({});
        setReferenciasTransferencia({});
        setErroresReferenciaTransferencia({});
        setModalOpen(false);
        setModalOpen2(false);
        setDescripcionAnulacion("");
        setDescripcionAnulacion2("");
    };

    const confirmarAnulacionVenta = async () => {
        if (!descripcionAnulacion.trim()) {
            mostraAlertaError("Debe ingresar una descripción para la anulación.", "Error");
            return;
        }

        try {
            setLoading(true);
            mostrarAlertaLoading("Anulando venta...");
            
            await AxiosPrivado.post(VentaAnulada, {
                ventaId: ventaSeleccionada.id,
                fechaHora: moment().format("YYYY-MM-DDTHH:mm:ssZ"),
                estado: "Anulada",
                empleadoId: usuario.datoPersonales.id,
                descripcion: descripcionAnulacion,
            });

            mostraAlertaOk("Venta anulada correctamente.", "Éxito");
            
            if (setListaProductos) ActualizarLista(ProductosListar, setListaProductos);
            if (setVentaSeleccionada) setVentaSeleccionada(null);
            
            limpiarDatosPago();
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (er) {
            ErroresPeticion(er);
        } finally {
            setLoading(false);
        }
    };

    const ultimoPago = useMemo(() => {
        if (!ventaSeleccionada) return null;
        return ventaSeleccionada.pagoVenta
            ?.filter(pv => pv.pago?.estado !== "Anulada" && parseFloat(pv.pago?.recibido) > 0)
            ?.map(pv => pv.pago)
            ?.sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0];
    }, [ventaSeleccionada]);

    const pagoYaAnulado = useMemo(() => {
        const pagos = ventaSeleccionada?.pagoVenta ?? [];
        const penultimoPago = pagos.length >= 2 ? pagos[pagos.length - 2] : null;
        return penultimoPago?.pago?.estado === "Anulada";
    }, [ventaSeleccionada]);

    const pagosValidos = useMemo(() => {
        if (!ventaSeleccionada) return [];
        return ventaSeleccionada.pagoVenta
            ?.filter(pv => pv.pago?.estado !== "Anulada")
            ?.map(pv => pv.pago)
            ?.sort((a, b) => new Date(a.fecha) - new Date(b.fecha)) || [];
    }, [ventaSeleccionada]);

    const saldoAntesDelUltimoPago = useMemo(() => {
        if (pagosValidos.length > 1) {
            const pagosSinUltimo = pagosValidos.slice(0, -1);
            const acumuladoSinUltimo = pagosSinUltimo.reduce(
                (sum, p) => sum + parseFloat(p.recibido || 0),
                0
            );
            return totalVenta - acumuladoSinUltimo;
        }
        return totalVenta;
    }, [pagosValidos, totalVenta]);

    const validarPago = (esCredito, total, posNum, transNum, efectivoNum, recibidoNum, pagos, pagoId) => {
        const totalTarjetaYTransf = posNum + transNum;
        if (!esCredito) {
            if (totalTarjetaYTransf > total) {
                mostraAlertaError("El monto pagado con tarjeta o transferencia no puede exceder el total de la compra.", "Pago inválido");
                return false;
            }
            const exceso = recibidoNum - total;
            const efectivoRequerido = total - totalTarjetaYTransf;
            if (exceso > 0 && efectivoNum < efectivoRequerido) {
                mostraAlertaError("El cambio solo puede generarse si el exceso proviene del efectivo.", "Pago inválido");
                return false;
            }
        } else {
            const pagadoSinEste = pagos?.filter(p => p.estado !== "Anulada" && p.id !== pagoId)
                ?.reduce((sum, p) => sum + parseFloat(p.recibido || 0), 0) || 0;
            const saldoActual = total - pagadoSinEste;

            if (recibidoNum > saldoActual) {
                if (totalTarjetaYTransf > saldoActual) {
                    mostraAlertaError("El monto pagado con tarjeta o transferencia no puede exceder el saldo pendiente.", "Pago inválido");
                    return false;
                }
                const efectivoRequerido = saldoActual - totalTarjetaYTransf;
                if (efectivoNum < efectivoRequerido) {
                    mostraAlertaError("El cambio solo puede generarse si el exceso proviene del efectivo.", "Pago inválido");
                    return false;
                }
            }
        }
        return true;
    };

    const validarReferencias = () => {
        let hayError = false;
        let nuevosErroresPos = {};
        let nuevosErroresTransferencia = {};

        Object.keys(montosPos).forEach(posId => {
            if (parseFloat(montosPos[posId] || 0) > 0) {
                const ref = referenciasPos[posId] || "";
                if (!/^\d{6,15}$/.test(ref)) {
                    nuevosErroresPos[posId] = "La referencia debe tener entre 6 y 15 dígitos numéricos.";
                    hayError = true;
                }
            }
        });

        Object.keys(montosTransferencia).forEach(cuentaId => {
            if (parseFloat(montosTransferencia[cuentaId] || 0) > 0) {
                const ref = referenciasTransferencia[cuentaId] || "";
                if (!/^\d{8,15}$/.test(ref)) {
                    nuevosErroresTransferencia[cuentaId] = "La referencia debe tener entre 8 y 15 dígitos numéricos.";
                    hayError = true;
                }
            }
        });

        setErroresReferenciaPos(nuevosErroresPos);
        setErroresReferenciaTransferencia(nuevosErroresTransferencia);
        if (hayError) {
            mostraAlertaError("Hay referencias inválidas. Verifica los campos marcados.", "Error de validación");
        }
        return !hayError;
    };

    const confirmarAnulacionPago = async () => {
        if (!validarReferencias()) return;

        const esCredito = ventaSeleccionada?.creditos?.length > 0;
        const efectivoNum = (datosPago.efectivo || []).reduce((acc, p) => acc + parseFloat(p.monto || 0), 0);
        const posNum = (datosPago.tarjeta || []).reduce((acc, p) => acc + parseFloat(p.monto || 0), 0);
        const transferenciaNum = (datosPago.transferencia || []).reduce((acc, p) => acc + parseFloat(p.monto || 0), 0);
        const totalRecibidoNum = efectivoNum + posNum + transferenciaNum;
        const totalElectronico = posNum + transferenciaNum;
        const saldoRequerido = saldoAntesDelUltimoPago;
        const pagoViejo = ultimoPago;
        const ventaId = ventaSeleccionada?.id;

        if (totalRecibidoNum <= 0) {
            mostraAlertaError("Debe ingresar al menos un método de pago válido.", "Error");
            return;
        }

        if (totalElectronico > saldoRequerido) {
            return mostraAlertaError(
                `La suma de tarjeta y transferencia no puede exceder el saldo pendiente.` + (saldoRequerido > 0 ? ` Saldo pendiente: L. ${saldoRequerido.toFixed(2)}` : ""),
                "Monto Inválido"
            );
        }

        if (!cajaId) return mostraAlertaError("Caja no autenticada");
        if (!cajaAbierta) return mostraAlertaError("La caja no está abierta. No se puede anular el pago.");
        if (descripcionAnulacion2.trim().length < 10) return mostraAlertaError("Ingrese una descripción detallada del motivo de anulación.");
        if (pagoYaAnulado) return mostraAlertaError("Este pago ya fue anulado. No puede repetirse el proceso.");
        if (!pagoViejo?.id || !ventaId) return mostraAlertaError("No se encontró información válida del pago o compra.");

        if (!validarPago(esCredito, totalVenta, posNum, transferenciaNum, efectivoNum, totalRecibidoNum, ventaSeleccionada?.pagos, pagoViejo.id)) {
            return;
        }

        const nuevoPago = {
            fecha: moment().toISOString(),
            pago: totalRecibidoNum,
            recibido: totalRecibidoNum,
            documento: pagoViejo.documento,
            cajaId,
            ventaId,
            estado: "Completada",
            efectivo: efectivoNum,
            transferencia: Object.keys(montosTransferencia).filter(c => parseFloat(montosTransferencia[c]) > 0).map(c => ({ monto: parseFloat(montosTransferencia[c]), referencia: referenciasTransferencia[c] || "", cuentaId: parseInt(c, 10) })),
            tarjeta: Object.keys(montosPos).filter(p => parseFloat(montosPos[p]) > 0).map(p => ({ monto: parseFloat(montosPos[p]), referencia: referenciasPos[p] || "", posId: parseInt(p, 10) }))
        };

        try {
            setLoading(true);
            mostrarAlertaLoading("Anulando pago...");

            await AxiosPrivado.post(VentaPagoAnulada, {
                pagoId: pagoViejo.id,
                fechaHora: moment().format("YYYY-MM-DDTHH:mm:ssZ"),
                empleadoId: usuario.datoPersonales.id,
                descripcion: descripcionAnulacion2,
                pago: nuevoPago,
            });

            mostraAlertaOk("Pago anulado y reemplazado correctamente.");
            if (setVentaSeleccionada) setVentaSeleccionada(null);
            limpiarDatosPago();
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (error) {
            ErroresPeticion(error);
        } finally {
            setLoading(false);
        }
    };

    const datosHeader = {
        titulo: {
            titulo: "Detalles de venta",
            url: "/app/admin/ventas/gestionar",
            tituloUrl: "Menu gestion",
            nombreUrl: "Edición",
        },
    };

    if (!ventaSeleccionada) return null;
    
const obtenerLineasDePagoDetalladas = (pagoVentaItem, indice) => {
        const lineas = [];

        const datosBase = {
            fecha: pagoVentaItem.pago.fecha,
            documento: pagoVentaItem.pago.documento || `Abono #${indice + 1}`,
            pagoId: pagoVentaItem.pago.id,
        };

        let detalleEncontrado = false;

        pagoVentaItem.pago.pagoefectivos?.forEach((efectivo, idx) => {
            detalleEncontrado = true;
            const cambio = parseFloat(pagoVentaItem.pago.recibido || 0) - parseFloat(pagoVentaItem.pago.pago || 0);
            lineas.push({
                ...datosBase,
                key: `${pagoVentaItem.pago.id}-efectivo-${idx}`,
                metodo: "Efectivo",

                // dinero recibido del cliente
                monto: parseFloat(efectivo.monto || 0),

                // dinero aplicado a la venta
                aplicado: parseFloat(efectivo.monto - cambio || 0),

                // cambio entregado
                cambio: cambio,

                cssClass: "text-success",
            });
        });

        pagoVentaItem.pago.pagotarjeta?.forEach((tarjeta, idx) => {
            detalleEncontrado = true;

            lineas.push({
                ...datosBase,
                key: `${pagoVentaItem.pago.id}-tarjeta-${idx}`,
                metodo: "Tarjeta",
                monto: parseFloat(tarjeta.monto || 0),
                aplicado: parseFloat(tarjeta.monto || 0),
                cambio: 0,
                cssClass: "text-info",
            });
        });

        pagoVentaItem.pago.pagotransferencia?.forEach((transferencia, idx) => {
            detalleEncontrado = true;

            lineas.push({
                ...datosBase,
                key: `${pagoVentaItem.pago.id}-transferencia-${idx}`,
                metodo: "Transferencia",
                monto: parseFloat(transferencia.monto || 0),
                aplicado: parseFloat(transferencia.monto || 0),
                cambio: 0,
                cssClass: "text-primary",
            });
        });

        const montoPagoPrincipal = parseFloat(
            pagoVentaItem.pago.recibido || 0
        );

        if (!detalleEncontrado && montoPagoPrincipal > 0) {
            lineas.push({
                ...datosBase,
                key: `${pagoVentaItem.pago.id}-general`,
                metodo: "Pago General",
                monto: montoPagoPrincipal,
                cssClass: "text-warning",
            });
        }

        return lineas;
    };
    const todasLasLineasDePago =
        ventaSeleccionada?.pagoVenta
            ?.filter(item => item.pago?.estado !== "Anulada")
            .sort(
                (a, b) =>
                    new Date(a.pago.fecha) -
                    new Date(b.pago.fecha)
            )
            .flatMap((item, indice) =>
                obtenerLineasDePagoDetalladas(item, indice)
            ) || [];

    const documentoInfo = (() => {
        if (ventaSeleccionada?.facturas?.length > 0) {
            return {
                tipo: "Factura",
                numero: ventaSeleccionada?.facturas[0].numeroFactura,
                badge: "bg-primary"
            };
        }

        if (ventaSeleccionada?.recibos?.length > 0) {
            return {
                tipo: "Recibo",
                numero: ventaSeleccionada?.recibos[0].id,
                badge: "bg-info"
            };
        }

        return null;
    })();

    return (
        <Page datos={datosHeader}>
            <Card
                titulo={`Detalles de la venta #${ventaSeleccionada.id}`}
                pie={
                    <>
                        {!(ventaSeleccionada.ventanuladas && ventaSeleccionada.ventanuladas.length > 0) ? (
                            <ModalBootstrap
                                titulo={`Anular Venta #${ventaSeleccionada?.id || "..."}`}
                                modalIsOpen={modalOpen}
                                setModalIsOpen={setModalOpen}
                                nombreBoton="Anular venta"
                                estiloBoton="btn btn-danger"
                                iconBoton="fas fa-ban"
                                pie={
                                    <button 
                                        className="btn btn-warning" 
                                        onClick={confirmarAnulacionVenta}
                                        disabled={loading}
                                    >
                                        {loading ? "Procesando..." : "Confirmar"}
                                    </button>
                                }
                            >
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Motivo de anulación</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={descripcionAnulacion}
                                        onChange={(e) => setDescripcionAnulacion(e.target.value)}
                                    />
                                </div>
                            </ModalBootstrap>
                        ) : (
                            <div className="card border-danger shadow-sm mt-3">
                                <div className="card-header bg-danger text-white">
                                    <div className="d-flex align-items-center">
                                        <i className="fas fa-ban me-2"></i>
                                        <strong>Venta Anulada</strong>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <small className="text-muted d-block">Motivo de Anulación</small>
                                            <div className="fw-bold">{ventaSeleccionada.ventanuladas[0]?.descripcion}</div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <small className="text-muted d-block">Responsable</small>
                                            <div className="fw-bold">{ventaSeleccionada.ventanuladas[0]?.Empleado?.nombreCompleto}</div>
                                        </div>
                                        <div className="col-md-6">
                                            <small className="text-muted d-block">Fecha de Anulación</small>
                                            <div className="fw-bold">
                                                {moment(ventaSeleccionada.ventanuladas[0]?.fechaHora).format("DD/MM/YYYY HH:mm")}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <small className="text-muted d-block">Estado</small>
                                            <span className="badge bg-danger">ANULADA</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {pagosValidos.length > 0 && !ventaSeleccionada?.ventanuladas?.length && ultimoPago && (
                            <ModalWizardBootstrap
                                titulo={`Proceso de anulación y nuevo pago para venta #${ventaSeleccionada?.id || "..."}`}
                                modalIsOpen={modalOpen2}
                                setModalIsOpen={setModalOpen2}
                                nombreBoton="Anular pago"
                                estiloBoton="btn btn-warning mx-3"
                                iconBoton="fas fa-exchange-alt mx-2"
                                iconos={["fas fa-ban", "fas fa-money-check", "fas fa-check-circle"]}
                                onFinish={confirmarAnulacionPago}
                                pasos={[
                                    {
                                        etiqueta: "Anulación",
                                        contenido: (
                                            <div className="mb-3">
                                                <label className="form-label fw-bold">Motivo de anulación</label>
                                                <textarea
                                                    className="form-control"
                                                    rows="3"
                                                    value={descripcionAnulacion2}
                                                    onChange={(e) => setDescripcionAnulacion2(e.target.value)}
                                                />
                                            </div>
                                        )
                                    }
                                ]}
                            />
                        )}
                    </>
                }
            >
                {ventaSeleccionada.ventadetallepedidos?.length > 0 ? (
                            <div className="container-fluid">
                                <div className="row justify-content-center">
                                    <div className="col-12">

                                        {/* ENCABEZADO */}
                                        <div className="card border-0 shadow-sm">

                                            <div className="card-header py-4">

                                                <div className="row mb-4">

                                                    <div className="col-md-8">

                                                        <h2 className="h3 mb-2 text-primary fw-bold">
                                                            Informe de Auditoría de Venta
                                                        </h2>

                                                        <p className="text-secondary mb-2">
                                                            Venta #{ventaSeleccionada.id}
                                                        </p>

                                                        <div>

                                                            {documentoInfo && (
                                                                <span
                                                                    className={`badge mr-2 ${documentoInfo.badge}`}
                                                                >
                                                                    {documentoInfo.tipo}: {documentoInfo.numero}
                                                                </span>
                                                            )}

                                                            {ventaSeleccionada.creditos?.[0] && (
                                                                <span className="badge bg-warning mr-2">
                                                                    Crédito #{ventaSeleccionada.creditos[0].id}
                                                                </span>
                                                            )}

                                                        </div>

                                                    </div>

                                                    <div className="col-md-4 text-md-right mt-3 mt-md-0">

                                                        <h4 className="mb-1 ">
                                                            Venta
                                                        </h4>

                                                        <p className="text-secondary mb-2">

                                                            Estado de la Venta

                                                        </p>

                                                        <span
                                                            className={`badge px-3 py-2 ${ventaSeleccionada.estado === "Completada"
                                                                ? "bg-success"
                                                                : ventaSeleccionada.estado === "Anulada"
                                                                    ? "bg-danger"
                                                                    : "bg-warning text-dark"
                                                                }`}
                                                        >
                                                            {ventaSeleccionada.estado}
                                                        </span>

                                                    </div>

                                                </div>

                                                <hr className="border-secondary opacity-50 my-4" />

                                                <div className="row">

                                                    {/* INFORMACIÓN DEL CLIENTE */}

                                                    <div className="col-md-6 text-md-left mt-4 mt-md-0">

                                                        <div className="row">
                                                            <div className="col-6 mb-2">
                                                                <small className="text-secondary d-block">
                                                                    Cliente
                                                                </small>


                                                                <strong>
                                                                    {ventaSeleccionada.Cliente?.ClienteEmpresas?.[0]?.nombre ||
                                                                        ventaSeleccionada.Cliente?.nombreCompleto ||
                                                                        "N/A"}
                                                                </strong>

                                                            </div>
                                                            <div className="col-6 mb-2">
                                                                <small className="text-secondary d-block">
                                                                    RTN
                                                                </small>


                                                                <strong>
                                                                    {ventaSeleccionada.rtn || "N/A"}
                                                                </strong>

                                                            </div>
                                                            <div className="col-6 mb-2">
                                                                <small className="text-secondary d-block">
                                                                    Tipo de Venta
                                                                </small>


                                                                <strong>
                                                                    {ventaSeleccionada.tipoVenta || "N/A"}
                                                                </strong>

                                                            </div>
                                                            <div className="col-6 mb-2">
                                                                <small className="text-secondary d-block">
                                                                    Vendedor
                                                                </small>


                                                                <strong>
                                                                    {ventaSeleccionada.Empleado?.nombreCompleto || "N/A"}
                                                                </strong>

                                                            </div>
                                                            <div className="col-6 mb-2">
                                                                <small className="text-secondary d-block">
                                                                    Caja
                                                                </small>

                                                                <strong>
                                                                    {ventaSeleccionada.caja?.nombre || "N/A"}
                                                                </strong>
                                                            </div>

                                                            <div className="col-6 mb-2">
                                                                <small className="text-secondary d-block">
                                                                    Dispositivo
                                                                </small>

                                                                <strong>
                                                                    {ventaSeleccionada.caja?.estacione?.dispositivo || "N/A"}
                                                                </strong>
                                                            </div>
                                                        </div>
                                                    </div>


                                                    {/* INFORMACIÓN DEL DOCUMENTO */}

                                                    <div className="col-md-6 text-md-right mt-4 mt-md-0">

                                                        <div className="row">

                                                            <div className="col-6 mb-2">
                                                                <small className="text-secondary d-block">
                                                                    {documentoInfo
                                                                        ? `${documentoInfo.tipo}`
                                                                        : "Documento"}
                                                                </small>

                                                                <strong>
                                                                    {documentoInfo
                                                                        ? `#${documentoInfo.numero}`
                                                                        : "N/A"}
                                                                </strong>
                                                            </div>

                                                            <div className="col-6 mb-2">
                                                                <small className="text-secondary d-block">
                                                                    Pedido
                                                                </small>

                                                                <strong>
                                                                    #
                                                                    {ventaSeleccionada.ventapedidos?.[0]?.pedidoId || "N/A"}
                                                                </strong>
                                                            </div>

                                                            {ventaSeleccionada.creditos?.[0] && (
                                                                <>
                                                                    <div className="col-6 mb-2">
                                                                        <small className="text-secondary d-block">
                                                                            Estado Crédito
                                                                        </small>

                                                                        <strong
                                                                            className={
                                                                                ventaSeleccionada.creditos[0].estado === "Pendiente"
                                                                                    ? "text-warning"
                                                                                    : "text-success"
                                                                            }
                                                                        >
                                                                            {ventaSeleccionada.creditos[0].estado}
                                                                        </strong>
                                                                    </div>

                                                                    <div className="col-6 mb-2">
                                                                        <small className="text-secondary d-block">
                                                                            Días Mora
                                                                        </small>

                                                                        <strong
                                                                            className={
                                                                                ventaSeleccionada.creditos[0].diasMora > 0
                                                                                    ? "text-danger"
                                                                                    : "text-success"
                                                                            }
                                                                        >
                                                                            {ventaSeleccionada.creditos[0].diasMora}
                                                                        </strong>
                                                                    </div>

                                                                    <div className="col-6 mb-2">
                                                                        <small className="text-secondary d-block">
                                                                            Fecha Registro
                                                                        </small>

                                                                        <strong>
                                                                            {moment(
                                                                                ventaSeleccionada.createdAt
                                                                            ).format("DD/MM/YYYY")}
                                                                        </strong>
                                                                    </div>

                                                                    <div className="col-6 mb-2">
                                                                        <small className="text-secondary d-block">
                                                                            Vencimiento
                                                                        </small>

                                                                        <strong>
                                                                            {moment(
                                                                                ventaSeleccionada.creditos[0].fechaVencimiento
                                                                            ).format("DD/MM/YYYY")}
                                                                        </strong>
                                                                    </div>
                                                                </>
                                                            )}

                                                        </div>

                                                    </div>

                                                </div>
                                            </div>

                                            <div className="card-body">

                                                {(() => {

                                                    const totalVenta = ventaSeleccionada.ventadetallepedidos.reduce(
                                                        (sum, item) =>
                                                            sum +
                                                            item.cantidad * parseFloat(item.precio || 0),
                                                        0
                                                    );

                                                    const totalPagado =
                                                        ventaSeleccionada.pagoVenta
                                                            ?.filter(p => p.pago.estado !== "Anulada")
                                                            .reduce(
                                                                (sum, p) =>
                                                                    sum +
                                                                    parseFloat(
                                                                        p.pago.pago || 0
                                                                    ),
                                                                0
                                                            ) || 0;

                                                    const saldoPendiente = Math.max(
                                                        totalVenta - totalPagado,
                                                        0
                                                    );

                                                    return (
                                                        <>
                                                            {/* KPI */}

                                                            {/* PRODUCTOS */}
                                                            <div className="card shadow-sm mb-4">

                                                                <div className="card-header">
                                                                    <h5 className="mb-0 font-weight-bold">
                                                                        Detalle de Productos Vendidos
                                                                    </h5>
                                                                </div>

                                                                <div className="card-body p-0">

                                                                    <div className="table-responsive">

                                                                        <table className="table align-middle mb-0">

                                                                            <thead>

                                                                                <tr>

                                                                                    <th
                                                                                        className="border-top-0"
                                                                                        style={{ width: "60px" }}
                                                                                    >
                                                                                        #
                                                                                    </th>

                                                                                    <th className="border-top-0">
                                                                                        Descripción
                                                                                    </th>

                                                                                    <th
                                                                                        className="border-top-0 text-right"
                                                                                        style={{ width: "120px" }}
                                                                                    >
                                                                                        Cantidad
                                                                                    </th>

                                                                                    <th
                                                                                        className="border-top-0 text-right"
                                                                                        style={{ width: "150px" }}
                                                                                    >
                                                                                        Precio Unitario
                                                                                    </th>

                                                                                    <th
                                                                                        className="border-top-0 text-right"
                                                                                        style={{ width: "150px" }}
                                                                                    >
                                                                                        Descuento
                                                                                    </th>

                                                                                    <th
                                                                                        className="border-top-0 text-right"
                                                                                        style={{ width: "180px" }}
                                                                                    >
                                                                                        Subtotal
                                                                                    </th>

                                                                                </tr>

                                                                            </thead>

                                                                            <tbody>

                                                                                {ventaSeleccionada.ventadetallepedidos.map(
                                                                                    (detalle, index) => {

                                                                                        const subtotal =
                                                                                            detalle.cantidad *
                                                                                            parseFloat(
                                                                                                detalle.precio || 0
                                                                                            );

                                                                                        return (
                                                                                            <tr key={index}>

                                                                                                <td>
                                                                                                    {index + 1}
                                                                                                </td>

                                                                                                <td>

                                                                                                    <div className="mb-2">

                                                                                                        <span className="badge badge-secondary mr-1">
                                                                                                            ID {detalle.producto?.id}
                                                                                                        </span>

                                                                                                        <span className="badge badge-dark">
                                                                                                            {detalle.producto?.codigo}
                                                                                                        </span>

                                                                                                    </div>

                                                                                                    <p className="mb-0 font-weight-bold">
                                                                                                        {detalle.producto?.nombre}
                                                                                                    </p>

                                                                                                    {detalle.tipoImpuesto && (
                                                                                                        <small className="text-muted">
                                                                                                            Impuesto: {detalle.tipoImpuesto}
                                                                                                        </small>
                                                                                                    )}

                                                                                                </td>

                                                                                                <td className="text-right">
                                                                                                    {detalle.cantidad}
                                                                                                </td>

                                                                                                <td className="text-right">
                                                                                                    {formatoMoneda(
                                                                                                        detalle.precio
                                                                                                    )}
                                                                                                </td>

                                                                                                <td className="text-right text-muted">
                                                                                                    {formatoMoneda(
                                                                                                        detalle.descuento
                                                                                                    )}
                                                                                                </td>

                                                                                                <td className="text-right font-weight-bold">
                                                                                                    {formatoMoneda(
                                                                                                        subtotal
                                                                                                    )}
                                                                                                </td>

                                                                                            </tr>
                                                                                        );
                                                                                    }
                                                                                )}

                                                                            </tbody>

                                                                            <tfoot>

                                                                                <tr>

                                                                                    <td
                                                                                        colSpan="5"
                                                                                        className="text-right font-weight-bold border-top"
                                                                                    >
                                                                                        Subtotal
                                                                                    </td>

                                                                                    <td className="text-right border-top">
                                                                                        {formatoMoneda(
                                                                                            ventaSeleccionada.subtotal
                                                                                        )}
                                                                                    </td>

                                                                                </tr>

                                                                                <tr>

                                                                                    <td
                                                                                        colSpan="5"
                                                                                        className="text-right font-weight-bold"
                                                                                    >
                                                                                        ISV 15%
                                                                                    </td>

                                                                                    <td className="text-right">
                                                                                        {formatoMoneda(
                                                                                            ventaSeleccionada.impuesto15
                                                                                        )}
                                                                                    </td>

                                                                                </tr>

                                                                                {Number(
                                                                                    ventaSeleccionada.impuesto18 || 0
                                                                                ) > 0 && (

                                                                                        <tr>

                                                                                            <td
                                                                                                colSpan="5"
                                                                                                className="text-right font-weight-bold"
                                                                                            >
                                                                                                ISV 18%
                                                                                            </td>

                                                                                            <td className="text-right">
                                                                                                {formatoMoneda(
                                                                                                    ventaSeleccionada.impuesto18
                                                                                                )}
                                                                                            </td>

                                                                                        </tr>

                                                                                    )}

                                                                                {Number(
                                                                                    ventaSeleccionada.exento || 0
                                                                                ) > 0 && (

                                                                                        <tr>

                                                                                            <td
                                                                                                colSpan="5"
                                                                                                className="text-right font-weight-bold"
                                                                                            >
                                                                                                Exento
                                                                                            </td>

                                                                                            <td className="text-right">
                                                                                                {formatoMoneda(
                                                                                                    ventaSeleccionada.exento
                                                                                                )}
                                                                                            </td>

                                                                                        </tr>

                                                                                    )}

                                                                                {Number(
                                                                                    ventaSeleccionada.exonerado || 0
                                                                                ) > 0 && (

                                                                                        <tr>

                                                                                            <td
                                                                                                colSpan="5"
                                                                                                className="text-right font-weight-bold"
                                                                                            >
                                                                                                Exonerado
                                                                                            </td>

                                                                                            <td className="text-right">
                                                                                                {formatoMoneda(
                                                                                                    ventaSeleccionada.exonerado
                                                                                                )}
                                                                                            </td>

                                                                                        </tr>

                                                                                    )}

                                                                                <tr>

                                                                                    <td
                                                                                        colSpan="5"
                                                                                        className="text-right font-weight-bold h6 border-top"
                                                                                    >
                                                                                        TOTAL FACTURADO
                                                                                    </td>

                                                                                    <td className="text-right font-weight-bold h6 border-top text-primary">
                                                                                        {formatoMoneda(
                                                                                            ventaSeleccionada.totalVenta
                                                                                        )}
                                                                                    </td>

                                                                                </tr>

                                                                            </tfoot>

                                                                        </table>

                                                                    </div>

                                                                </div>

                                                            </div>

                                                            <div className="row">

                                                                {/* CONCILIACION */}
                                                                <div className="col-lg-4 mb-4">

                                                                    <div className="card shadow-sm border-primary">

                                                                        <div className="card-header bg-primary text-white">
                                                                            Conciliación Financiera
                                                                        </div>

                                                                        <div className="card-body">

                                                                            <div className="d-flex justify-content-between mb-3">
                                                                                <span>
                                                                                    Total Facturado
                                                                                </span>

                                                                                <strong>
                                                                                    {formatoMoneda(
                                                                                        totalVenta
                                                                                    )}
                                                                                </strong>
                                                                            </div>

                                                                            <div className="d-flex justify-content-between mb-3">
                                                                                <span>
                                                                                    Total Pagado
                                                                                </span>

                                                                                <strong className="text-success">
                                                                                    {formatoMoneda(
                                                                                        totalPagado
                                                                                    )}
                                                                                </strong>
                                                                            </div>

                                                                            <hr />

                                                                            <div className="d-flex justify-content-between">

                                                                                <span className="fw-bold">
                                                                                    Saldo Pendiente
                                                                                </span>

                                                                                <span
                                                                                    className={`fw-bold fs-4 ${saldoPendiente > 0
                                                                                        ? "text-danger"
                                                                                        : "text-success"
                                                                                        }`}
                                                                                >
                                                                                    {formatoMoneda(
                                                                                        saldoPendiente
                                                                                    )}
                                                                                </span>

                                                                            </div>

                                                                        </div>

                                                                    </div>

                                                                    {/* DATOS AUDITORIA */}

                                                                    <div className="card shadow-sm mt-3">

                                                                        <div className="card-header bg-info text-white">
                                                                            Información de Auditoría
                                                                        </div>

                                                                        <div className="card-body">

                                                                            <p>
                                                                                <strong>Productos:</strong>{" "}
                                                                                {
                                                                                    ventaSeleccionada
                                                                                        .ventadetallepedidos
                                                                                        .length
                                                                                }
                                                                            </p>

                                                                            <p className="mb-0">
                                                                                <strong>
                                                                                    Registros de pago:
                                                                                </strong>{" "}
                                                                                {
                                                                                    ventaSeleccionada
                                                                                        .pagoVenta
                                                                                        ?.length
                                                                                }
                                                                            </p>

                                                                        </div>

                                                                    </div>

                                                                </div>

                                                                {/* PAGOS */}
                                                                <div className="col-lg-8">

                                                                    <div className="card shadow-sm">

                                                                        <div className="card-header">
                                                                            <h5 className="mb-0 font-weight-bold">
                                                                                Libro Auxiliar de Pagos
                                                                            </h5>
                                                                        </div>

                                                                        <div className="card-body p-0">

                                                                            <div className="table-responsive">

                                                                                <table className="table align-middle mb-0">

                                                                                    <thead>

                                                                                        <tr>

                                                                                            <th
                                                                                                className="border-top-0"
                                                                                                style={{ width: "100px" }}
                                                                                            >
                                                                                                Pago
                                                                                            </th>

                                                                                            <th
                                                                                                className="border-top-0"
                                                                                                style={{ width: "170px" }}
                                                                                            >
                                                                                                Fecha
                                                                                            </th>

                                                                                            <th className="border-top-0">
                                                                                                Método
                                                                                            </th>

                                                                                            <th
                                                                                                className="border-top-0 text-right"
                                                                                                style={{ width: "140px" }}
                                                                                            >
                                                                                                Recibido
                                                                                            </th>

                                                                                            <th
                                                                                                className="border-top-0 text-right"
                                                                                                style={{ width: "140px" }}
                                                                                            >
                                                                                                Cambio
                                                                                            </th>

                                                                                            <th
                                                                                                className="border-top-0 text-right"
                                                                                                style={{ width: "140px" }}
                                                                                            >
                                                                                                Aplicado
                                                                                            </th>

                                                                                        </tr>

                                                                                    </thead>

                                                                                    <tbody>

                                                                                        {todasLasLineasDePago.length > 0 ? (

                                                                                            todasLasLineasDePago.map(
                                                                                                linea => (

                                                                                                    <tr key={linea.key}>

                                                                                                        <td>

                                                                                                            <p className="mb-0 font-weight-bold">
                                                                                                                #{linea.pagoId}
                                                                                                            </p>

                                                                                                            <small className="text-muted">
                                                                                                                {linea.documento}
                                                                                                            </small>

                                                                                                        </td>

                                                                                                        <td>

                                                                                                            <div>
                                                                                                                {moment(
                                                                                                                    linea.fecha
                                                                                                                ).format(
                                                                                                                    "DD/MM/YYYY"
                                                                                                                )}
                                                                                                            </div>

                                                                                                            <small className="text-muted">
                                                                                                                {moment(
                                                                                                                    linea.fecha
                                                                                                                ).format(
                                                                                                                    "HH:mm"
                                                                                                                )}
                                                                                                            </small>

                                                                                                        </td>

                                                                                                        <td>

                                                                                                            <span
                                                                                                                className={`badge ${linea.metodo === "Efectivo"
                                                                                                                    ? "badge-success"
                                                                                                                    : linea.metodo === "Tarjeta"
                                                                                                                        ? "badge-info"
                                                                                                                        : linea.metodo === "Transferencia"
                                                                                                                            ? "badge-primary"
                                                                                                                            : "badge-secondary"
                                                                                                                    }`}
                                                                                                            >
                                                                                                                {linea.metodo}
                                                                                                            </span>

                                                                                                        </td>

                                                                                                        <td className="text-right font-weight-bold">

                                                                                                            {formatoMoneda(
                                                                                                                linea.monto
                                                                                                            )}

                                                                                                        </td>

                                                                                                        <td className="text-right text-danger">

                                                                                                            {Number(
                                                                                                                linea.cambio || 0
                                                                                                            ) > 0
                                                                                                                ? formatoMoneda(
                                                                                                                    linea.cambio
                                                                                                                )
                                                                                                                : "-"}

                                                                                                        </td>

                                                                                                        <td className="text-right font-weight-bold text-success">

                                                                                                            {formatoMoneda(
                                                                                                                linea.aplicado
                                                                                                            )}

                                                                                                        </td>

                                                                                                    </tr>

                                                                                                )
                                                                                            )

                                                                                        ) : (

                                                                                            <tr>

                                                                                                <td
                                                                                                    colSpan="6"
                                                                                                    className="text-center text-muted py-4"
                                                                                                >
                                                                                                    No existen pagos registrados.
                                                                                                </td>

                                                                                            </tr>

                                                                                        )}

                                                                                    </tbody>

                                                                                    <tfoot>

                                                                                        <tr>

                                                                                            <td
                                                                                                colSpan="5"
                                                                                                className="text-right font-weight-bold border-top"
                                                                                            >
                                                                                                Total Aplicado
                                                                                            </td>

                                                                                            <td className="text-right font-weight-bold text-success border-top">

                                                                                                {formatoMoneda(totalPagado)}

                                                                                            </td>

                                                                                        </tr>

                                                                                    </tfoot>

                                                                                </table>

                                                                            </div>

                                                                        </div>

                                                                    </div>

                                                                </div>

                                                            </div>

                                                            {/* ANULADOS */}

                                                            {ventaSeleccionada.pagoVenta?.filter(
                                                                p => p.pago.estado === "Anulada"
                                                            ).length > 0 && (

                                                                    <div className="card shadow-sm mt-4 border-danger">

                                                                        <div className="card-header">

                                                                            <div className="d-flex justify-content-between align-items-center">

                                                                                <div>

                                                                                    <h5 className="mb-0 text-danger font-weight-bold">
                                                                                        Pagos Anulados
                                                                                    </h5>

                                                                                    <small className="text-muted">
                                                                                        Movimientos excluidos de la conciliación financiera y del cálculo del saldo pendiente.
                                                                                    </small>

                                                                                </div>

                                                                                <span className="badge badge-danger">
                                                                                    {
                                                                                        ventaSeleccionada.pagoVenta.filter(
                                                                                            p => p.pago.estado === "Anulada"
                                                                                        ).length
                                                                                    } Anulado(s)
                                                                                </span>

                                                                            </div>

                                                                        </div>

                                                                        <div className="card-body p-0">

                                                                            <div className="table-responsive">

                                                                                <table className="table align-middle mb-0">

                                                                                    <thead>

                                                                                        <tr>

                                                                                            <th className="border-top-0">
                                                                                                Pago
                                                                                            </th>

                                                                                            <th className="border-top-0">
                                                                                                Fecha
                                                                                            </th>

                                                                                            <th className="border-top-0">
                                                                                                Motivo
                                                                                            </th>

                                                                                            <th className="border-top-0">
                                                                                                Método
                                                                                            </th>

                                                                                            <th
                                                                                                className="border-top-0 text-right"
                                                                                                style={{ width: "180px" }}
                                                                                            >
                                                                                                Monto
                                                                                            </th>

                                                                                        </tr>

                                                                                    </thead>

                                                                                    <tbody>

                                                                                        {ventaSeleccionada.pagoVenta
                                                                                            .filter(
                                                                                                p =>
                                                                                                    p.pago.estado ===
                                                                                                    "Anulada"
                                                                                            )
                                                                                            .map(p => (

                                                                                                <tr key={p.pago.id}>

                                                                                                    <td>

                                                                                                        <p className="mb-0 font-weight-bold">
                                                                                                            #{p.pago.id}
                                                                                                        </p>

                                                                                                        <small className="text-danger">
                                                                                                            Anulado
                                                                                                        </small>

                                                                                                    </td>

                                                                                                    <td>

                                                                                                        <div>
                                                                                                            {moment(
                                                                                                                p.pago.fecha
                                                                                                            ).format(
                                                                                                                "DD/MM/YYYY"
                                                                                                            )}
                                                                                                        </div>

                                                                                                        <small className="text-muted">
                                                                                                            {moment(
                                                                                                                p.pago.fecha
                                                                                                            ).format(
                                                                                                                "HH:mm"
                                                                                                            )}
                                                                                                        </small>

                                                                                                    </td>

                                                                                                    <td>

                                                                                                        <p className="mb-0">
                                                                                                            {p.pago.pagoanulados[0].descripcion || "Sin motivo"}
                                                                                                        </p>
                                                                                                        <small className="text-muted">
                                                                                                            {p.pago.pagoanulados[0].Empleado?.nombreCompleto || "Desconocido"}
                                                                                                        </small>

                                                                                                    </td>

                                                                                                    <td>

                                                                                                        {p.pago.pagoefectivos?.length > 0 && (
                                                                                                            <div className="d-flex justify-content-between align-items-center mb-1">

                                                                                                                <span>
                                                                                                                    <span className="badge badge-success mr-2">
                                                                                                                        Efectivo
                                                                                                                    </span>
                                                                                                                </span>

                                                                                                                <strong>
                                                                                                                    {formatoMoneda(
                                                                                                                        p.pago.pagoefectivos.reduce(
                                                                                                                            (s, e) => s + Number(e.monto || 0),
                                                                                                                            0
                                                                                                                        )
                                                                                                                    )}
                                                                                                                </strong>

                                                                                                            </div>
                                                                                                        )}

                                                                                                        {p.pago.pagotarjeta?.length > 0 && (
                                                                                                            <div className="d-flex justify-content-between align-items-center mb-1">

                                                                                                                <span>
                                                                                                                    <span className="badge badge-info mr-2">
                                                                                                                        POS
                                                                                                                    </span>
                                                                                                                </span>

                                                                                                                <strong>
                                                                                                                    {formatoMoneda(
                                                                                                                        p.pago.pagotarjeta.reduce(
                                                                                                                            (s, e) => s + Number(e.monto || 0),
                                                                                                                            0
                                                                                                                        )
                                                                                                                    )}
                                                                                                                </strong>

                                                                                                            </div>
                                                                                                        )}

                                                                                                        {p.pago.pagotransferencia?.length > 0 && (
                                                                                                            <div className="d-flex justify-content-between align-items-center mb-1">

                                                                                                                <span>
                                                                                                                    <span className="badge badge-primary mr-2">
                                                                                                                        Transferencia
                                                                                                                    </span>
                                                                                                                </span>

                                                                                                                <strong>
                                                                                                                    {formatoMoneda(
                                                                                                                        p.pago.pagotransferencia.reduce(
                                                                                                                            (s, e) => s + Number(e.monto || 0),
                                                                                                                            0
                                                                                                                        )
                                                                                                                    )}
                                                                                                                </strong>

                                                                                                            </div>
                                                                                                        )}

                                                                                                        {(Number(p.pago.recibido || 0) >
                                                                                                            Number(p.pago.pago || 0)) && (

                                                                                                                <>
                                                                                                                    <hr className="my-1" />

                                                                                                                    <div className="d-flex justify-content-between align-items-center">

                                                                                                                        <span className="text-danger">
                                                                                                                            <i className="fas fa-undo-alt mr-1"></i>
                                                                                                                            Cambio
                                                                                                                        </span>

                                                                                                                        <strong className="text-danger">
                                                                                                                            {formatoMoneda(
                                                                                                                                Number(p.pago.recibido || 0) -
                                                                                                                                Number(p.pago.pago || 0)
                                                                                                                            )}
                                                                                                                        </strong>

                                                                                                                    </div>
                                                                                                                </>
                                                                                                            )}

                                                                                                    </td>

                                                                                                    <td className="text-right font-weight-bold text-danger">

                                                                                                        {formatoMoneda(
                                                                                                            p.pago.pago
                                                                                                        )}

                                                                                                    </td>

                                                                                                </tr>

                                                                                            ))}

                                                                                    </tbody>

                                                                                    <tfoot>

                                                                                        <tr>

                                                                                            <td
                                                                                                colSpan="3"
                                                                                                className="text-right font-weight-bold border-top"
                                                                                            >
                                                                                                Total Anulado
                                                                                            </td>

                                                                                            <td className="text-right font-weight-bold text-danger border-top">

                                                                                                {formatoMoneda(
                                                                                                    ventaSeleccionada.pagoVenta
                                                                                                        .filter(
                                                                                                            p =>
                                                                                                                p.pago.estado ===
                                                                                                                "Anulada"
                                                                                                        )
                                                                                                        .reduce(
                                                                                                            (sum, p) =>
                                                                                                                sum +
                                                                                                                Number(
                                                                                                                    p.pago.pago ||
                                                                                                                    0
                                                                                                                ),
                                                                                                            0
                                                                                                        )
                                                                                                )}

                                                                                            </td>

                                                                                        </tr>

                                                                                    </tfoot>

                                                                                </table>

                                                                            </div>

                                                                        </div>

                                                                    </div>
                                                                )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="alert alert-info text-center py-4 shadow-sm" role="alert">
                                <h5 className="mb-0">ℹ️ No hay detalles o movimientos de pago registrados para esta venta.</h5>
                            </div>
                        )}
                    </Card>
        </Page>
    );
};

export default VerVenta;